import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../hooks/useChat';

interface ChatProps {
  token: string | null;
  purchaseId: string;
  tokensRemaining: number;
  onTokensUpdate: (remaining: number) => void;
}

export function Chat({ token, purchaseId, tokensRemaining, onTokensUpdate }: ChatProps) {
  const [input, setInput] = useState('');
  const { messages, loading, error, sendMessage } = useChat(token);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const inputText = input;
    setInput('');

    try {
      const response = await sendMessage(inputText, purchaseId);
      onTokensUpdate(response.tokens_remaining);
    } catch (err) {
      console.error(err);
    }
  };

  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 24);
  const timeLeft = expiresAt.toLocaleTimeString('vi-VN');

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold">Claude 1 Ngày</h1>
          <p className="text-sm text-gray-500">Hôm nay thử, ngày mai quyết định</p>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-blue-600">{tokensRemaining}M / Tokens</div>
          <div className="text-xs text-gray-500">Hết hạn: {timeLeft}</div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">👋</div>
              <p className="text-gray-500 text-lg">Bắt đầu cuộc trò chuyện của bạn</p>
              <p className="text-gray-400 text-sm mt-2">Gõ tin nhắn bên dưới</p>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              {msg.tokens_used > 0 && (
                <p className="text-xs mt-1 opacity-70">
                  📊 Tokens: {msg.tokens_used}
                </p>
              )}
            </div>
          </div>
        ))}

        {error && (
          <div className="flex justify-start">
            <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg">
              ❌ {error}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-2 rounded-lg">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        {tokensRemaining <= 0 && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded-lg mb-4">
            ⚠️ Tokens hết! Vui lòng mua thêm
          </div>
        )}
        <form onSubmit={handleSend} className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Nhập tin nhắn..."
            disabled={loading || tokensRemaining <= 0}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={loading || !input.trim() || tokensRemaining <= 0}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            {loading ? '...' : 'Gửi'}
          </button>
        </form>
      </div>
    </div>
  );
}
